import { assertNever, invariant } from "../core/assert";
import { canonicalJson } from "../core/canonical";
import { fnv1a64Hex } from "../core/hash";
import { EventQueue, type QueueState, type Scheduled } from "../core/queue";
import { err, ok, type Result } from "../core/result";
import { Rng, type RngState } from "../core/rng";
import {
  type Bps,
  type Cents,
  type CompanyId,
  type GameTime,
  gameTime,
  HOUR,
  id,
} from "../core/units";
import { handleCompetitorTake, type NewsItem } from "../economy/competition";
import {
  applyAcceptContract,
  applyBreakContract,
  applyReleaseContract,
  breakPenalty,
  type ContractState,
  handleContractDelivered,
  handleContractExpired,
  handleContractFailed,
  handleMarketTick,
  handleRotationDue,
  type OfferEstimate,
  offerEstimate,
  openMarket,
  validateAcceptContract,
  validateBreakContract,
  validateReleaseContract,
} from "../economy/contracts";
import { corridorIndex, initialCorridors } from "../economy/corridors";
import { chargeTrip, handleDayStart, scheduleDays } from "../economy/costs";
import {
  applyBuyLicence,
  applySetInsurance,
  dailyPremium,
  validateBuyLicence,
  validateSetInsurance,
} from "../economy/insurance";
import {
  balanceOf,
  freezeLedger,
  type IncomeStatement,
  incomeStatement,
  type LedgerEntry,
  post,
} from "../economy/ledger";
import { initialOil, type OilState } from "../economy/oil";
import { initialReputation } from "../economy/reputation";
import { applySubcontract, validateSubcontract } from "../economy/subcontract";
import {
  applyDriveVehicle,
  applyLoadShipment,
  applyRerouteVehicle,
  applyUnloadShipment,
  handleServiceDone,
  type InitialShipment,
  type InitialVehicle,
  onTripArrived,
  type ShipmentState,
  setupFleet,
  startService,
  type VehicleState,
  validateDriveVehicle,
  validateLoadShipment,
  validateRerouteVehicle,
  validateUnloadShipment,
} from "../fleet/fleet";
import {
  applyAcquireVehicle,
  applyDisposeVehicle,
  validateAcquireVehicle,
  validateDisposeVehicle,
  vehicleValue,
} from "../fleet/ownership";
import {
  applyAssignContract,
  applySetPlan,
  applyUnassignContract,
  validateAssignContract,
  validateSetPlan,
  validateUnassignContract,
} from "../fleet/tour";
import { applyServiceVehicle, validateServiceVehicle } from "../fleet/upkeep";
import {
  applyCloseWarehouse,
  applyOpenWarehouse,
  storedVolume,
  validateCloseWarehouse,
  validateOpenWarehouse,
  type WarehouseState,
} from "../fleet/warehouses";
import { type HazardCalendar, handleHazardDay, openCalendar } from "../hazards/calendar";
import {
  type HazardSpec,
  type HazardState,
  handleHazardEnd,
  handleHazardStart,
  scheduleHazards,
} from "../hazards/hazards";
import { createRouter, type Router } from "../network/astar";
import {
  forgetLegs,
  handleTripArrived,
  handleTripHalted,
  startTrip,
  type TripState,
  validateStartTrip,
} from "../network/trips";
import {
  evaluateGoals,
  handleLevelEnd,
  type LevelEvaluation,
  type LevelGoals,
  scheduleLevelEnd,
} from "../progression/objectives";
import type { Command, CommandError, LoggedCommand } from "./commands";
import type { GameData, SimContext } from "./context";
import { applyPing, handlePong, validatePing } from "./diagnostics";
import { EVENT_PRIORITY, type GameEvent } from "./events";
import {
  applySetStandingOrders,
  applyStandingOrders,
  type OrderLogEntry,
  validateSetStandingOrders,
} from "./orders";
import type { CompanyState, Pong, WorldState } from "./state";

/** Everything needed to start a game from scratch; with the journal, enough to replay it. */
export type GameConfig = {
  readonly seed: string;
  /** Scenario identifier (level, challenge, sandbox). */
  readonly scenario: string;
  /** Version of `@kargo/data` the game was started with. */
  readonly dataVersion: string;
  /** City pack (ADR 0006) and its version: replays must load exactly this data. */
  readonly pack: PackRef;
  /** Companies present at start; the engine sorts them by id. */
  readonly companies: readonly InitialCompany[];
  readonly startTime?: number;
  /** Scripted hazards of the scenario (roles already resolved to graph edges). */
  readonly hazards?: readonly HazardSpec[];
  /** Level hazard calendar: rush hours and random jams, drawn day by day. */
  readonly hazardCalendar?: HazardCalendar;
  /** Level goals: objectives per star, evaluated at `end` (and on demand for progress). */
  readonly goals?: LevelGoals;
  /** Vehicles and shipments present at start (SER-140; later from contracts and the shop). */
  readonly vehicles?: readonly InitialVehicle[];
  readonly shipments?: readonly InitialShipment[];
  /** Spot market (needs `data.market`): first tick time; absent = no market. */
  readonly market?: { readonly firstTick: number };
};

export type PackRef = { readonly id: string; readonly version: string };

/** A company at game start; `cash` (cents) is booked as the opening balance. */
export type InitialCompany = { readonly id: string; readonly cash?: number };

/** Everything that determines the future of the game. Plain canonical data. */
export type CanonicalGameState = {
  readonly seed: string;
  readonly world: WorldState;
  readonly queue: QueueState<GameEvent>;
  readonly rng: RngState;
};

/** A restorable point in time: the state plus how much of the journal it already includes. */
export type Snapshot = {
  readonly time: GameTime;
  readonly journalLength: number;
  readonly state: CanonicalGameState;
};

/** Read-only views for the UI. Never hand out the mutable state itself. */
export type GameQuery = {
  readonly time: () => GameTime;
  readonly companies: () => readonly Readonly<CompanyState>[];
  /** Oil price today and its recent history (SER-274); null without an oil market. */
  readonly oil: () => Readonly<OilState> | null;
  /** Price index of each corridor now (SER-275); empty without corridor pricing. */
  readonly corridors: () => Readonly<Record<string, Bps>>;
  /** Market news, newest last (SER-276). */
  readonly news: () => readonly NewsItem[];
  /** Standing orders carried out, newest last (SER-279). */
  readonly orderLog: () => readonly OrderLogEntry[];
  readonly pongs: () => readonly Pong[];
  readonly pendingEvents: () => number;
  readonly trips: () => readonly Readonly<TripState>[];
  readonly vehicles: () => readonly Readonly<VehicleState>[];
  readonly shipments: () => readonly Readonly<ShipmentState>[];
  /** Cash position of a company (sum of its ledger entries). */
  readonly balance: (companyId: CompanyId) => Cents;
  /** The company warehouses and what they hold (SER-271). */
  readonly warehouses: () => readonly (Readonly<WarehouseState> & { readonly stored: number })[];
  /** What a vehicle would sell for now: its book value if owned, 0 if leased (SER-269). */
  readonly vehicleValue: (vehicleId: string) => Cents;
  /** Today's insurance premium of a company, if it took the policy (SER-293). */
  readonly insurancePremium: (companyId: CompanyId) => Cents;
  /** What breaking a recurring agreement would cost now (SER-293). */
  readonly breakPenalty: (contractId: string) => Cents;
  readonly ledger: (companyId: CompanyId) => readonly LedgerEntry[];
  readonly incomeStatement: (companyId: CompanyId, from: number, to: number) => IncomeStatement;
  /** Offers on the board and contracts taken. */
  readonly contracts: () => readonly Readonly<ContractState>[];
  /** Expected cost and margin of a contract for the market's reference vehicle. */
  readonly offerEstimate: (contractId: string) => OfferEstimate | null;
  /** Hazards in force. */
  readonly hazards: () => readonly Readonly<HazardState>[];
  /** Progress towards the level goals now (null without goals). */
  readonly progress: (companyId: CompanyId) => LevelEvaluation | null;
  /** Final outcome, once the level has ended. */
  readonly outcome: (companyId: CompanyId) => LevelEvaluation | null;
};

/** Technical cadence of in-memory snapshots (not game balance). */
const SNAPSHOT_EVERY_COMMANDS = 25;
const SNAPSHOT_EVERY_SECONDS = 6 * HOUR;
/**
 * Snapshots kept in memory (SER-284): long levels (30 to 90 days) would otherwise keep
 * hundreds of full copies. Recent ones are all kept; older ones are thinned out, one in two,
 * so rewinding far back replays a little more of the journal.
 */
const MAX_SNAPSHOTS = 24;

/** One running game: world state, event queue, random streams, command journal and snapshots. */
export class Game {
  readonly config: GameConfig;
  readonly data: GameData;
  private state: WorldState;
  private routerCache: Router | undefined;
  private queue: EventQueue<GameEvent>;
  private rng: Rng;
  private journal: LoggedCommand[];
  private snapshots: Snapshot[] = [];

  readonly query: GameQuery = {
    time: () => this.state.time,
    companies: () => this.state.companies.map((c) => copy(c)),
    oil: () => (this.state.oil === undefined ? null : copy(this.state.oil)),
    news: () => (this.state.news ?? []).slice(),
    orderLog: () => (this.state.orderLog ?? []).slice(),
    corridors: () => {
      const out: Record<string, Bps> = {};
      for (const key of Object.keys(this.state.corridors ?? {}).sort()) {
        const index = corridorIndex(this.ctx, key);
        if (index !== null) out[key] = index;
      }
      return out;
    },
    pongs: () => this.state.diagnostics.pongs.slice(),
    pendingEvents: () => this.queue.size,
    trips: () => this.state.trips.map((t) => ({ ...t, legs: t.legs.slice() })),
    vehicles: () => this.state.vehicles.map((v) => ({ ...v, plan: v.plan.slice() })),
    shipments: () => this.state.shipments.map((s) => ({ ...s })),
    balance: (companyId) => balanceOf(this.state, companyId),
    warehouses: () =>
      this.state.warehouses.map((w) => ({ ...w, stored: storedVolume(this.state, w) })),
    vehicleValue: (vehicleId) => {
      const v = this.state.vehicles.find((x) => x.id === vehicleId);
      return v ? vehicleValue(this.ctx, v) : (0 as Cents);
    },
    insurancePremium: (companyId) => dailyPremium(this.ctx, companyId),
    breakPenalty: (contractId) => {
      const k = this.state.contracts.find((x) => x.id === contractId);
      return k ? breakPenalty(k) : (0 as Cents);
    },
    ledger: (companyId) => this.state.ledger.entries.filter((e) => e.companyId === companyId),
    incomeStatement: (companyId, from, to) =>
      incomeStatement(this.state, companyId, gameTime(from), gameTime(to)),
    contracts: () => this.state.contracts.map((c) => ({ ...c })),
    offerEstimate: (contractId) => {
      const c = this.state.contracts.find((x) => x.id === contractId);
      return c ? offerEstimate(this.data, c) : null;
    },
    hazards: () => this.state.hazards.slice(),
    progress: (companyId) =>
      this.config.goals ? evaluateGoals(this.state, this.data, companyId, this.config.goals) : null,
    outcome: (companyId) =>
      this.state.outcomes?.find((o) => o.companyId === companyId)?.evaluation ?? null,
  };

  /** The narrow view rule modules work through (getters: state and rng change on rewind). */
  private readonly ctx: SimContext = ((game: Game): SimContext => ({
    get state() {
      return game.state;
    },
    get data() {
      return game.data;
    },
    get rng() {
      return game.rng;
    },
    router: () => game.router(),
    schedule: (time, event, key) => game.schedule(time, event, key),
    invalidate: (key) => {
      game.queue.invalidate(key);
    },
    calendar: () => game.config.hazardCalendar,
    goals: () => game.config.goals,
  }))(this);

  private constructor(
    config: GameConfig,
    from: Snapshot,
    journal: readonly LoggedCommand[],
    data: GameData,
  ) {
    this.config = config;
    this.data = data;
    Object.defineProperties(this.ctx, {
      state: { get: () => this.state },
      data: { get: () => this.data },
      rng: { get: () => this.rng },
    });
    const s = copy(from.state);
    invariant(s.seed === config.seed, "snapshot belongs to another seed");
    this.state = s.world;
    this.queue = new EventQueue(s.queue);
    this.rng = new Rng(s.seed, s.rng);
    this.journal = journal.slice(0, from.journalLength);
    this.snapshots.push(from);
    freezeLedger(this.state);
  }

  /** New game at its start time. */
  static create(config: GameConfig, data: GameData = {}): Game {
    const ids = config.companies.map((c) => id<"Company">(c.id)).sort(compareIds);
    for (let i = 1; i < ids.length; i++) {
      invariant(ids[i] !== ids[i - 1], `duplicate company ${ids[i]}`);
    }
    const time = gameTime(config.startTime ?? 0);
    const world: WorldState = {
      time,
      ...(data.oil === undefined ? {} : { oil: initialOil(data.oil) }),
      ...(data.market?.corridors === undefined
        ? {}
        : { corridors: initialCorridors(data.market.zoneDemand) }),
      companies: ids.map((cid) =>
        data.reputation === undefined
          ? { id: cid }
          : { id: cid, reputation: initialReputation(data.reputation) },
      ),
      diagnostics: { pongs: [] },
      trips: [],
      hazards: [],
      nextTripSeq: 0,
      vehicles: [],
      shipments: [],
      ledger: { entries: [], nextSeq: 0 },
      contracts: [],
      nextContractSeq: 0,
      nextVehicleSeq: 0,
      warehouses: [],
      nextWarehouseSeq: 0,
      outcomes: null,
    };
    const state: CanonicalGameState = {
      seed: config.seed,
      world,
      queue: new EventQueue<GameEvent>().state(),
      rng: new Rng(config.seed).state(),
    };
    const game = new Game(config, { time, journalLength: 0, state: copy(state) }, [], data);
    const opening = [...config.companies]
      .filter((c) => (c.cash ?? 0) !== 0)
      .sort((a, b) => compareIds(a.id, b.id));
    for (const c of opening) {
      post(game.ctx, {
        companyId: id<"Company">(c.id),
        account: "equity.opening",
        amount: c.cash as Cents,
        ref: "start",
      });
    }
    const hazards = config.hazards ?? [];
    const vehicles = config.vehicles ?? [];
    const shipments = config.shipments ?? [];
    if (
      opening.length + hazards.length + vehicles.length + shipments.length > 0 ||
      config.market ||
      config.hazardCalendar ||
      config.goals ||
      data.costs
    ) {
      scheduleDays(game.ctx, time);
      scheduleHazards(game.ctx, hazards);
      if (config.hazardCalendar) openCalendar(game.ctx, config.hazardCalendar);
      if (config.goals) scheduleLevelEnd(game.ctx, config.goals);
      setupFleet(game.ctx, vehicles, shipments);
      if (config.market) openMarket(game.ctx, Math.max(time, config.market.firstTick));
      game.snapshots = [game.snapshot()];
    }
    return game;
  }

  /**
   * Game resumed from a snapshot. `journal` is the full history up to the snapshot
   * (kept for rewind and server replay); the snapshot must include all of it.
   */
  static restore(
    config: GameConfig,
    snapshot: Snapshot,
    journal: readonly LoggedCommand[],
    data: GameData = {},
  ): Game {
    invariant(
      snapshot.journalLength === journal.length,
      "snapshot does not cover the whole journal",
    );
    const game = new Game(config, snapshot, journal, data);
    // Without the earlier snapshots, rewinding before this point replays from the start.
    game.snapshots.unshift(Game.create(config, data).snapshots[0] as Snapshot);
    return game;
  }

  /** Replays a journal from the start of the game, then advances to `until` (default: last command). */
  static replay(
    config: GameConfig,
    journal: readonly LoggedCommand[],
    until?: number,
    data: GameData = {},
  ): Game {
    const game = Game.create(config, data);
    game.replayFrom(journal, until);
    return game;
  }

  /** Validates then applies a command at the current game time. Invalid commands change nothing. */
  dispatch(command: Command): Result<null, CommandError> {
    const checked = validate(this.ctx, command);
    if (!checked.ok) return checked;
    this.maybeSnapshot();
    this.journal.push({ time: this.state.time, seq: this.journal.length, command });
    this.apply(command);
    return ok(null);
  }

  /** Processes every event due at or before `t`, then sets the clock to `t`. */
  advanceTo(t: number): void {
    const target = gameTime(t);
    invariant(target >= this.state.time, `advanceTo(${target}) is before now (${this.state.time})`);
    for (let e = this.queue.popDue(target); e !== undefined; e = this.queue.popDue(target)) {
      this.handle(e);
    }
    this.state.time = target;
  }

  /** Jumps to the next event time and processes every event due then. Returns that time, if any. */
  advanceToNextEvent(): GameTime | undefined {
    const next = this.queue.peek();
    if (next === undefined) return undefined;
    this.advanceTo(next.time);
    return next.time;
  }

  /**
   * Puts the game back as it was at time `t`, keeping the commands issued up to `t`
   * and forgetting the rest: restore the closest snapshot, then replay the journal.
   */
  rewindTo(t: number): void {
    const target = gameTime(t);
    invariant(target <= this.state.time, `rewindTo(${target}) is after now (${this.state.time})`);
    let base: Snapshot | undefined;
    for (const s of this.snapshots) if (s.time <= target) base = s;
    invariant(base !== undefined, `rewindTo(${target}) is before the start of the game`);

    const journal = this.journal;
    const restored = copy(base.state);
    this.state = restored.world;
    this.queue = new EventQueue(restored.queue);
    this.rng = new Rng(restored.seed, restored.rng);
    freezeLedger(this.state);
    this.journal = journal.slice(0, base.journalLength);
    this.snapshots = this.snapshots.filter(
      (s) => s.time <= base.time && s.journalLength <= base.journalLength,
    );
    this.replayFrom(
      journal.slice(base.journalLength).filter((entry) => entry.time <= target),
      target,
    );
  }

  /**
   * An independent copy of the game from now, for "what if" evaluations: its state goes
   * through `transform` (e.g. a frozen world), it has no journal and no calendar, and
   * nothing done to it reaches this game.
   */
  fork(transform: (s: CanonicalGameState) => CanonicalGameState = (s) => s): Game {
    const { hazardCalendar: _, ...config } = this.config;
    const state = transform(copy(this.canonicalState()));
    const fork = new Game(
      config,
      { time: this.state.time, journalLength: 0, state },
      [],
      this.data,
    );
    fork.routerCache = this.routerCache;
    return fork;
  }

  /** A restorable copy of the current state. */
  snapshot(): Snapshot {
    return {
      time: this.state.time,
      journalLength: this.journal.length,
      state: copy(this.canonicalState()),
    };
  }

  /**
   * Everything that determines the future of the game: world, pending events and
   * random streams. The command journal is history, not state, and is left out.
   */
  canonicalState(): CanonicalGameState {
    return {
      seed: this.config.seed,
      world: this.state,
      queue: this.queue.state(),
      rng: this.rng.state(),
    };
  }

  /** Deterministic fingerprint of `canonicalState()` (FNV-1a 64, hex). */
  hash(): string {
    return fnv1a64Hex(canonicalJson(this.canonicalState()));
  }

  log(): readonly LoggedCommand[] {
    return this.journal.slice();
  }

  private replayFrom(entries: readonly LoggedCommand[], until?: number): void {
    for (const entry of entries) {
      invariant(entry.seq === this.journal.length, `journal gap at seq ${entry.seq}`);
      this.advanceTo(entry.time);
      const result = this.dispatch(entry.command);
      invariant(result.ok, `journal command ${entry.seq} no longer valid on replay`);
    }
    if (until !== undefined) this.advanceTo(until);
  }

  private router(): Router {
    if (!this.routerCache) {
      invariant(this.data.network !== undefined, "no road network loaded");
      this.routerCache = createRouter(this.data.network);
    }
    return this.routerCache;
  }

  private maybeSnapshot(): void {
    const last = this.snapshots[this.snapshots.length - 1];
    if (
      last === undefined ||
      this.journal.length - last.journalLength >= SNAPSHOT_EVERY_COMMANDS ||
      this.state.time - last.time >= SNAPSHOT_EVERY_SECONDS
    ) {
      this.snapshots.push(this.snapshot());
      if (this.snapshots.length > MAX_SNAPSHOTS) {
        const recent = this.snapshots.slice(-MAX_SNAPSHOTS / 2);
        const older = this.snapshots.slice(0, -MAX_SNAPSHOTS / 2).filter((_, i) => i % 2 === 0);
        this.snapshots = [...older, ...recent];
      }
    }
  }

  private schedule(time: GameTime, event: GameEvent, key?: string): void {
    invariant(time >= this.state.time, `cannot schedule ${event.type} in the past`);
    const priority = EVENT_PRIORITY[event.type];
    this.queue.push(time, event, key === undefined ? { priority } : { priority, key });
  }

  private apply(command: Command): void {
    switch (command.type) {
      case "ping":
        applyPing(this.ctx, command);
        return;
      case "startTrip":
        startTrip(this.ctx, command);
        return;
      case "driveVehicle":
        applyDriveVehicle(this.ctx, command);
        return;
      case "loadShipment":
        applyLoadShipment(this.ctx, command);
        return;
      case "unloadShipment":
        applyUnloadShipment(this.ctx, command);
        return;
      case "acceptContract":
        applyAcceptContract(this.ctx, command);
        return;
      case "rerouteVehicle":
        applyRerouteVehicle(this.ctx, command);
        return;
      case "assignContract":
        applyAssignContract(this.ctx, command);
        return;
      case "setPlan":
        applySetPlan(this.ctx, command);
        return;
      case "unassignContract":
        applyUnassignContract(this.ctx, command);
        return;
      case "releaseContract":
        applyReleaseContract(this.ctx, command);
        return;
      case "acquireVehicle":
        applyAcquireVehicle(this.ctx, command);
        return;
      case "disposeVehicle":
        applyDisposeVehicle(this.ctx, command);
        return;
      case "serviceVehicle":
        applyServiceVehicle(this.ctx, command, (v, seconds) => startService(this.ctx, v, seconds));
        return;
      case "openWarehouse":
        applyOpenWarehouse(this.ctx, command);
        return;
      case "closeWarehouse":
        applyCloseWarehouse(this.ctx, command);
        return;
      case "breakContract":
        applyBreakContract(this.ctx, command);
        return;
      case "setInsurance":
        applySetInsurance(this.ctx, command);
        return;
      case "buyLicence":
        applyBuyLicence(this.ctx, command);
        return;
      case "subcontract":
        applySubcontract(this.ctx, command);
        return;
      case "setStandingOrders":
        applySetStandingOrders(this.ctx, command);
        return;
      default:
        assertNever(command, "command");
    }
  }

  private handle(entry: Scheduled<GameEvent>): void {
    this.state.time = entry.time;
    const event = entry.event;
    switch (event.type) {
      case "pong":
        handlePong(this.ctx, event);
        return;
      case "tripArrived":
        {
          const trip = handleTripArrived(this.ctx, event);
          chargeTrip(this.ctx, trip);
          onTripArrived(this.ctx, trip);
          forgetLegs(trip);
        }
        return;
      case "serviceDone":
        handleServiceDone(this.ctx, event);
        return;
      case "marketTick":
        handleMarketTick(this.ctx, event);
        return;
      case "contractExpired":
        handleContractExpired(this.ctx, event);
        return;
      case "contractFailed":
        handleContractFailed(this.ctx, event);
        return;
      case "rotationDue":
        handleRotationDue(this.ctx, event);
        return;
      case "competitorTake":
        handleCompetitorTake(this.ctx, event);
        return;
      case "dayStart":
        handleDayStart(this.ctx, event);
        return;
      case "contractDelivered":
        handleContractDelivered(this.ctx, event);
        return;
      case "tripHalted":
        handleTripHalted(this.ctx, event);
        return;
      case "hazardStart":
        {
          // Trips a closure now blocks: standing orders act at once (SER-279).
          const before = new Set(
            this.state.trips.filter((t) => t.haltLeg !== null).map((t) => `${t.id}:${t.version}`),
          );
          handleHazardStart(this.ctx, event);
          for (const trip of this.state.trips)
            if (
              trip.status !== "arrived" &&
              trip.haltLeg !== null &&
              !before.has(`${trip.id}:${trip.version}`)
            )
              applyStandingOrders(this.ctx, trip);
        }
        return;
      case "hazardEnd":
        handleHazardEnd(this.ctx, event);
        return;
      case "hazardDay":
        handleHazardDay(this.ctx, event);
        return;
      case "levelEnd":
        handleLevelEnd(this.ctx, event);
        return;
      default:
        assertNever(event, "event");
    }
  }
}

export function createGame(config: GameConfig, data: GameData = {}): Game {
  return Game.create(config, data);
}

/** Deep, independent copy through the canonical form (also checks the state is canonical). */
function copy<T>(value: T): T {
  return JSON.parse(canonicalJson(value)) as T;
}

/** Pure validation: reads the state, never mutates it. */
function validate(ctx: SimContext, command: Command): Result<null, CommandError> {
  switch (command.type) {
    case "ping":
      return validatePing(ctx, command);
    case "startTrip":
      return validateStartTrip(ctx, command);
    case "driveVehicle":
      return validateDriveVehicle(ctx, command);
    case "loadShipment":
      return validateLoadShipment(ctx, command);
    case "unloadShipment":
      return validateUnloadShipment(ctx, command);
    case "acceptContract":
      return validateAcceptContract(ctx, command);
    case "rerouteVehicle":
      return validateRerouteVehicle(ctx, command);
    case "assignContract":
      return validateAssignContract(ctx, command);
    case "setPlan":
      return validateSetPlan(ctx, command);
    case "unassignContract":
      return validateUnassignContract(ctx, command);
    case "releaseContract":
      return validateReleaseContract(ctx, command);
    case "acquireVehicle":
      return validateAcquireVehicle(ctx, command);
    case "disposeVehicle":
      return validateDisposeVehicle(ctx, command);
    case "serviceVehicle":
      return validateServiceVehicle(ctx, command);
    case "openWarehouse":
      return validateOpenWarehouse(ctx, command);
    case "closeWarehouse":
      return validateCloseWarehouse(ctx, command);
    case "breakContract":
      return validateBreakContract(ctx, command);
    case "setInsurance":
      return validateSetInsurance(ctx, command);
    case "buyLicence":
      return validateBuyLicence(ctx, command);
    case "subcontract":
      return validateSubcontract(ctx, command);
    case "setStandingOrders":
      return validateSetStandingOrders(ctx, command);
    default:
      // Commands may come from untrusted JSON (saves, replays): reject unknown types.
      return err({ code: "unknownCommand", type: String((command as { type: unknown }).type) });
  }
}

function compareIds(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
